/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {Children, ReactNode} from 'react';
import isFunction from 'lodash.isfunction';

// Enum defining controls for If and Else components
enum Control {
  If = 'If',
  Else = 'Else'
}

// Interface for components that only expect children as props
interface ChildrenProps {
  children: ReactNode;
}

// Type representing components with added control information
type ControlType = {
  control: Control;
};

// Alias ChildrenProps as ChildrenResults for clarity in control assignment
type ChildrenResults = React.JSX.Element & ControlType;

// Alias ChildrenProps as If and Else components
const If   = ({children}: ChildrenProps) => <>{children}</>;
const Else = ({children}: ChildrenProps) => <>{children}</>;

// Assign control property to If and Else components using TypeScript assertion
(If as unknown as ChildrenResults).control   = Control.If;
(Else as unknown as ChildrenResults).control = Control.Else;

/**
 * Props interface for ControlRenderer component.
 *
 * @param condition - Boolean or a function returning a boolean to determine rendering.
 * @param strict - Optional boolean flag for strict equality check if condition is a boolean.
 * @param children - Tuple array containing React elements: [If component, Else component].
 */
interface ConditionalRenderProps extends ChildrenProps {
  condition: boolean | string | number | Function;
  strict?: boolean;
}

/**
 * @deprecated The `ControlRenderer` component should no longer be used. For conditional
 * rendering, the {@link Render} component must be used exclusively, which can be imported
 * from `@ion/dls/src/render/Render`
 *
 * ControlRenderer component conditionally renders its children based on the given condition.
 * If the condition evaluates to true, it renders the If child.
 * If the condition evaluates to false, it renders the Else child, if provided.
 *
 * @param condition - Boolean or function returning a boolean to determine which child to render.
 * @param strict - Optional boolean flag for strict equality check if condition is a boolean.
 * @param children - Tuple array containing React elements: [If component, Else component]
 *
 * @returns Rendered ReactElement of either If or Else component based on condition.
 *
 * ```typescript
 * import {ControlRenderer, If, Else} from '@/dls/src/render/ControlRenderer';
 *
 * // Basic usage:
 * <ControlRenderer condition={true}>
 *   <If>
 *     Rendered when condition is true
 *   </If>
 *   <Else>
 *     Rendered when condition is false
 *   </Else>
 * </ControlRenderer>
 *
 * // Condition as a function:
 * const checkCondition = () => true;
 * <ControlRenderer condition={checkCondition}>
 *   <If>
 *     Rendered when condition is true
 *   </If>
 *   <Else>
 *     Rendered when condition is false
 *   </Else>
 * </ControlRenderer>
 *
 * // Only with If condition
 * <ControlRenderer condition={checkCondition}>
 *   <If>
 *     Rendered when condition is true
 *   </If>
 * </ControlRenderer>
 * ```
 */
const ControlRenderer = ({
  condition,
  children,
  strict = false
}: ConditionalRenderProps) => {
  const test    = (isFunction(condition) ? (condition as Function)() : condition);
  const isValid = strict ? test === true : !!test;
  let ifContent;
  let elseContent;

  Children.forEach(children, child => {
    if (React.isValidElement(child)) {
      const {control} = (child.type || {}) as unknown as ControlType;
      if (control === Control.If) {
        ifContent = child;
      } else if (control === Control.Else) {
        elseContent = child;
      }
    }
  });

  return isValid ? ifContent : elseContent;
};

export {ControlRenderer, If, Else};

export default ControlRenderer;
